import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  question: 'What services does Hardy Blend offer?',
  answer: 'Hardy Blend specializes in web development, mobile app development (iOS & Android), UI/UX design, e-commerce solutions, backend development, and cloud services. We provide end-to-end digital solutions tailored to your business needs.'
}, {
  question: 'How long does it take to build a website?',
  answer: "The timeline varies based on project complexity. A simple website typically takes 2-4 weeks, while complex web applications can take 8-12 weeks or more. We'll provide a detailed timeline during our initial consultation."
}, {
  question: 'Do you develop mobile apps for both iOS and Android?',
  answer: "Yes, we develop native apps for both iOS and Android, as well as cross-platform solutions using React Native and Flutter. We'll recommend the best approach based on your requirements and budget."
}, {
  question: 'What is your development process?',
  answer: "We follow an agile methodology: Discovery & Planning → Design & Prototyping → Development → Testing & QA → Launch → Post-Launch Support. You'll receive regular updates and have opportunities to provide feedback throughout."
}, {
  question: 'How much does a website or app cost?',
  answer: 'Costs vary based on features, complexity, and timeline. Simple websites start from £2,000, while complex web applications and mobile apps range from £10,000-£50,000+. Contact us for a detailed quote tailored to your project.'
}, {
  question: 'Do you provide ongoing maintenance and support?',
  answer: "Yes, we offer comprehensive maintenance packages including security updates, bug fixes, performance optimization, and feature enhancements. We're committed to your long-term success."
}, {
  question: 'Can you redesign my existing website?',
  answer: "Absolutely! We specialize in website redesigns and modernization. We'll analyze your current site, identify improvements, and create a modern, responsive design that enhances user experience and conversions."
}, {
  question: 'Do you offer e-commerce development?',
  answer: 'Yes, we build robust e-commerce platforms with secure payment gateways, inventory management, order tracking, and conversion-optimized designs. We work with platforms like Shopify, WooCommerce, and custom solutions.'
}, {
  question: 'Will my website be mobile-friendly?',
  answer: 'All our websites are fully responsive and mobile-first, ensuring perfect functionality across all devices. With over 60% of web traffic from mobile, this is a standard practice in all our projects.'
}, {
  question: 'Do you provide SEO services?',
  answer: 'Yes, we implement SEO best practices including optimized code, fast loading speeds, proper meta tags, schema markup, and semantic HTML. We can also provide ongoing SEO services to improve your search rankings.'
}, {
  question: 'What technologies do you use?',
  answer: "We use modern, industry-standard technologies including React, Next.js, Node.js, TypeScript, Tailwind CSS for web, and React Native/Flutter for mobile. We choose the best tech stack for each project's specific needs."
}, {
  question: 'Can you integrate third-party services and APIs?',
  answer: 'Yes, we have extensive experience integrating payment gateways (Stripe, PayPal), CRM systems, email marketing tools, analytics platforms, social media APIs, and custom third-party services.'
}, {
  question: 'Do you sign NDAs and protect intellectual property?',
  answer: "Absolutely. We're happy to sign NDAs and confidentiality agreements. Your intellectual property and project details are completely protected and remain your property."
}, {
  question: 'What is your payment structure?',
  answer: 'We typically work with milestone-based payments: 30% upfront, 40% at mid-project milestone, and 30% upon completion. For larger projects, we can discuss custom payment schedules that work for both parties.'
}, {
  question: 'Can you help with app store submission?',
  answer: 'Yes, we handle the entire app store submission process for both Apple App Store and Google Play Store, including preparing assets, writing descriptions, and managing the review process.'
}, {
  question: 'Do you provide hosting services?',
  answer: "While we don't provide hosting directly, we'll help you choose the best hosting solution (AWS, Google Cloud, Vercel, etc.) and handle the complete deployment and setup process."
}, {
  question: 'How do you ensure website security?',
  answer: 'We implement industry-standard security practices including SSL certificates, secure authentication, data encryption, regular security audits, protection against common vulnerabilities, and compliance with GDPR and data protection regulations.'
}, {
  question: 'Can you migrate my existing website to a new platform?',
  answer: "Yes, we handle website migrations including content transfer, database migration, URL redirects, and ensuring zero downtime. We'll create a detailed migration plan to ensure a smooth transition."
}, {
  question: 'What makes Hardy Blend different from other agencies?',
  answer: 'We combine technical expertise with creative innovation, maintain transparent communication, deliver on time, provide competitive pricing, and offer ongoing support. Our focus is on building long-term partnerships, not just completing projects.'
}, {
  question: 'How can I get started with Hardy Blend?',
  answer: "Simply contact us via our contact form, WhatsApp (+44 7921 876472), or email (info@hardyblend.com). We'll schedule a free consultation to discuss your project, provide recommendations, and create a detailed proposal."
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section id="faq" className="py-20 bg-slate-900" data-id="element-83">
      <div className="container mx-auto px-4" data-id="element-84">
        <div className="text-center mb-16" data-id="element-85">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-86">
            Frequently Asked Questions
          </h2>
          <div className="w-20 h-1 bg-red-600 mx-auto mb-6" data-id="element-87"></div>
          <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-88">
            Find answers to common questions about our services, process, and
            how we can help your business succeed.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-4" data-id="element-89">
          {faqs.map((faq, index) => <div key={index} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden" data-id="element-90">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-750 transition-colors" data-id="element-91">
                <span className="text-lg font-semibold text-white pr-8" data-id="element-92">
                  {faq.question}
                </span>
                <ChevronDown className={`w-5 h-5 text-red-500 flex-shrink-0 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-93" />
              </button>

              <AnimatePresence data-id="element-94">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-95">
                    <div className="px-6 pb-6 text-slate-400 leading-relaxed" data-id="element-96">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
      </div>
    </section>;
}